import { CommonModule } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ImgFallbackDirective } from "@shared/directives/img-fallback/img-fallback.directive";
import { DateTime } from "luxon";
import { environment } from "../../../environments/environment";
import { Order } from "../../../models/order";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ResourceAssignment } from "../../../models/user";
import { LocalStorageService } from "../../services/local-storage.service";
import { PurchaseSuccessService } from "../../services/purchase-success.service";
import { MarketingPixelService } from "../../services/marketing/marketing-pixel.service";

@Component({
  selector: "app-compra-realizada",
  imports: [
    AppLayoutComponent,
    ButtonComponent,
    CommonModule,
    ImgFallbackDirective,
  ],
  templateUrl: "./compra-realizada.component.html",
  styleUrl: "./compra-realizada.component.scss",
})
export class CompraRealizadaComponent implements OnInit {
  order: Order | null = null;
  loading = true;
  error: string | null = null;
  orderId: number | null = null;
  isSeller = false;
  private readonly marketingPixels = inject(MarketingPixelService);

  // Estado do pagamento decide a tela: só é "sucesso" quando o pagamento foi de fato aprovado.
  // Uma cobrança recusada (falha/antifraude) NUNCA pode mostrar a tela de compra realizada.
  private static readonly PAID_STATUSES = ["paid", "courtesy"];
  private static readonly PENDING_STATUSES = [
    "waiting_payment",
    "processing",
    "pending",
  ];

  get isPaid(): boolean {
    return (
      !!this.order &&
      CompraRealizadaComponent.PAID_STATUSES.includes(this.order.payment_status)
    );
  }

  get isPending(): boolean {
    return (
      !!this.order &&
      CompraRealizadaComponent.PENDING_STATUSES.includes(
        this.order.payment_status,
      )
    );
  }

  get isFailed(): boolean {
    return !!this.order && !this.isPaid && !this.isPending;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private purchaseSuccessService: PurchaseSuccessService,
    private localStorageService: LocalStorageService,
  ) {}

  ngOnInit(): void {
    // Tentar obter orderId da URL ou localStorage
    this.route.queryParams.subscribe((params) => {
      const queryOrderId = Number(params["orderId"]);
      this.orderId =
        Number.isSafeInteger(queryOrderId) && queryOrderId > 0
          ? queryOrderId
          : null;
      this.loadPurchaseData();
    });
  }

  loadPurchaseData(): void {
    this.loading = true;
    this.error = null;

    if (this.orderId) {
      // Buscar compra específica por ID
      this.purchaseSuccessService.getPurchaseById(this.orderId).subscribe({
        next: (order) => {
          this.order = order;
          this.refreshSellerFlag();
          this.loading = false;
          // Only a specific authorized order can request a descriptor. The existing
          // latest-order display fallback below is never a purchase authorization.
          if (this.orderId && order.payment_status === "paid")
            this.marketingPixels.purchase(this.orderId);
        },
        error: (error) => {
          console.error("Erro ao buscar compra:", error);
          this.error = "Erro ao carregar dados da compra";
          this.loading = false;
        },
      });
    } else {
      // Buscar compra mais recente
      this.purchaseSuccessService.getLatestPurchase().subscribe({
        next: (order) => {
          this.order = order;
          this.refreshSellerFlag();
          this.loading = false;
        },
        error: (error) => {
          console.error("Erro ao buscar compra mais recente:", error);
          this.error = "Erro ao carregar dados da compra";
          this.loading = false;
        },
      });
    }
  }

  goToPedidos() {
    this.router.navigate([this.isSeller ? "/seller-orders" : "/my-orders"]);
  }

  private refreshSellerFlag(): void {
    const eventIds = new Set(
      (this.order?.tickets ?? []).map((ticket) => ticket.event_id),
    );

    if (eventIds.size === 0) {
      this.isSeller = false;
      return;
    }

    const raw = this.localStorageService.getItem("current_user");
    const user = raw
      ? (JSON.parse(raw) as {
          seller_assignments?: Array<number | ResourceAssignment>;
        })
      : null;
    const assignments = user?.seller_assignments ?? [];

    this.isSeller = assignments.some((assignment) => {
      if (typeof assignment === "number") {
        return eventIds.has(assignment);
      }

      return (
        assignment.resource_type === "Event" &&
        eventIds.has(assignment.resource_id)
      );
    });
  }

  goToHome() {
    this.router.navigate(["/"]);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  getTotalTickets(): number {
    if (!this.order?.tickets) return 0;
    return this.order.tickets.reduce((total, ticket) => total + 1, 0);
  }

  getEventTitle(): string {
    if (!this.order?.tickets || this.order.tickets.length === 0)
      return "Evento";
    return this.order.tickets[0].event_title || "Evento";
  }

  getEventDate(): Date | string {
    if (!this.order?.tickets || this.order.tickets.length === 0) return "";
    const ticket = this.order.tickets[0];
    const starDate = new Date(ticket.event_start_date);
    return starDate;
  }

  getEventTime(): string {
    if (!this.order?.tickets?.length) return "";

    const ticket = this.order.tickets[0];

    const start = DateTime.fromISO(ticket.event_start_date);
    const end = DateTime.fromISO(ticket.event_end_date);

    return `${start.toFormat("HH:mm")} - ${end.toFormat("HH:mm")}`;
  }

  getEventLocation(): string {
    if (!this.order?.tickets || this.order.tickets.length === 0) return "";
    const ticket = this.order.tickets[0];
    return `${ticket.event_location.location_name}, ${ticket.event_location.city_name} - ${ticket.event_location.state}`;
  }

  getPaymentStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      succeeded: "Pago",
      paid: "Pago",
      courtesy: "Cortesia",
      pending: "Pendente",
      waiting_payment: "Aguardando pagamento",
      processing: "Processando",
      failed: "Falhou",
      refused: "Recusado",
      expired: "Expirado",
      cancelled: "Cancelado",
      canceled: "Cancelado",
      refunded: "Reembolsado",
    };

    return statusMap[status] || status;
  }

  getPaymentMethodText(paymentMethod: string): string {
    const paymentMethodMap: Record<string, string> = {
      credit_card: "Cartão de Crédito",
      debit_card: "Cartão de Débito",
      pix: "PIX",
      billet: "Boleto",
      cash: "Dinheiro",
      complimentary: "Gratuito",
    };

    return paymentMethodMap[paymentMethod] || paymentMethod;
  }

  loadEventImage(event_thumb: string | undefined): string {
    if (
      !event_thumb ||
      event_thumb === "" ||
      event_thumb === "FALTA IMPLEMENTAR"
    ) {
      return "";
    }
    return `${environment.BLOB_URL}/${event_thumb}`;
  }
}
